import { createContext } from "react"

export const MODE_ALLOW = "allow"
export const MODE_DENY = "deny"

export const FIELD_PREFIX = "binary_restriction"
export const RULES_FIELD = `${FIELD_PREFIX}[binary_rules_attributes]`

export const SIGNING_STATE_ALL = "All"

export type Mode = typeof MODE_ALLOW | typeof MODE_DENY

export type PrimaryType = "bundleId" | "cdhash" | "teamId" | "signingId"
export type RefinementType = "teamId" | "signingId" | "pathPrefix"
export type IdentifierType = PrimaryType | RefinementType

export const PRIMARY_TYPES: PrimaryType[] = [
  "bundleId",
  "cdhash",
  "teamId",
  "signingId",
]

// Identifiers that can serve as the required primary identifier, by mode.
export const PRIMARY_OPTIONS: Record<Mode, PrimaryType[]> = {
  [MODE_ALLOW]: ["bundleId", "cdhash", "teamId"],
  [MODE_DENY]: PRIMARY_TYPES,
}

// Text identifiers that can be AND'd onto a primary as refinements.
export const REFINEMENT_TYPES: RefinementType[] = [
  "teamId",
  "signingId",
  "pathPrefix",
]

// A bundle ID targets the app list and a code directory hash is an exact match,
// so neither can be narrowed by another identifier.
export const REFINABLE_PRIMARY_TYPES: PrimaryType[] = ["teamId", "signingId"]

export const CODE_SIGNING_TYPES: IdentifierType[] = [
  "cdhash",
  "teamId",
  "signingId",
  "pathPrefix",
]

export interface BinaryRuleFields {
  displayName: string
  primaryType: PrimaryType | ""
  bundleId: string
  cdhash: string
  signingId: string
  teamId: string
  pathPrefix: string
  signingStates: string[]
}

export type BinaryRuleErrors = Partial<
  Record<keyof BinaryRuleFields | "base", string[]>
>

export type BinaryRuleData = {
  [Field in keyof BinaryRuleFields]?: BinaryRuleFields[Field] | null
} & {
  errors?: BinaryRuleErrors | null
}

export interface BinaryRuleItem extends BinaryRuleFields {
  key: number
  refinementTypes: RefinementType[]
  errors: BinaryRuleErrors
}

export interface BinaryRestrictionErrors {
  binaryRules?: string[]
}

export interface BinaryRestriction {
  id?: number
  name?: string
  description?: string
  mode?: Mode
  managedAppsAlwaysAllowed?: boolean
  appleAppsAlwaysAllowed?: boolean
  simplemdmIosAlwaysAllowed?: boolean
  simplemdmMacosAlwaysAllowed?: boolean
  simplemdmIntuneAlwaysAllowed?: boolean
  binaryRules: BinaryRuleData[],
  errors?: BinaryRestrictionErrors | null
}

export type TooltipKey =
  | IdentifierType
  | "alwaysAllowManagedApps"
  | "alwaysAllowAppleApps"
  | "appleAppsCaution"
  | "simplemdmIosCaution"
  | "simplemdmMacosCaution"
  | "simplemdmIntuneCaution"

export interface BinaryRestrictionMeta {
  tooltips: Record<TooltipKey, string>
  signingStateOptions: string[]
}

interface BinaryFormContextValue {
  mode: Mode
  disabled: boolean
  signingStateOptions: string[]
  tooltips: BinaryRestrictionMeta["tooltips"]
}

export const BinaryFormContext = createContext<BinaryFormContextValue>({
  mode: MODE_DENY,
  disabled: false,
  signingStateOptions: [],
  tooltips: {} as BinaryRestrictionMeta["tooltips"],
})
